import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';

import classNames from 'classnames';

import {
  Button, Dimmer, Loader, Pagination, Popup, Table
} from 'semantic-ui-react';

import '../css/LtiContentPickerSearchView.less';

import { register } from '../i18n';
import { DIALOG_NAMES } from '../managers/DialogManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';
import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';
import LtiService from '../services/LtiService';
import PopupService from '../services/PopupService';
import UtilityService from '../services/UtilityService';

const t = register('LtiContentPickerSearchView');

const AssessmentItemFilter = SatCoreComponent('AssessmentItemFilter');
const IndeterminateCheckbox = SatCoreComponent('IndeterminateCheckbox');
const LTIPreview = SatCoreComponent('LTIPreview');
const PopupButton = SatCoreComponent('PopupButton');

const isStudentAssignable = (deliveryMode) => {
  return deliveryMode && (deliveryMode.indexOf('assign') > -1 || deliveryMode === 'student_always');
};

const LtiContentPickerSearchView = observer(({ history }) => {
  const { contentManager, courseManager, dialogManager, ltiManager, navigationManager } = useContext(MobXProviderContext);

  const [isSubmittingLtiContentPickerSelections, setIsSubmittingLtiContentPickerSelections] = useState(false);
  const [previewCourseElement, setPreviewCourseElement] = useState(null);

  useEffect(() => {
    navigationManager.setView(VIEW_SELECTION.LTI_CONTENT_PICKER_SEARCH);
  }, []);

  const handleBackToContent = () => {
    navigationManager.setView(VIEW_SELECTION.LTI_CONTENT_PICKER);
    history.push(`/ltiContentPicker?view=${VIEW_SELECTION.LTI_CONTENT_PICKER}`);
  };

  const handleAddContent = async () => {
    const selections = ltiManager.collectLtiContentPickerSelections();

    if (selections.length === 0) {
      dialogManager.setOpenDialog(DIALOG_NAMES.TEXT, {
        message: t('noContentSelected'),
        size: 'tiny',
        title: t('noContentSelectedTitle')
      },
      () => dialogManager.closeDialog(DIALOG_NAMES.TEXT));
      return;
    }

    await submitLtiContentPickerSelectionsAndClose(selections);
  };

  const submitLtiContentPickerSelectionsAndClose = async (selections) => {
    setIsSubmittingLtiContentPickerSelections(true);

    try {
      const result = await LtiService.submitContentPickerSelections(
        ltiManager.ltiClassroomId,
        selections,
        ltiManager.ltiJwtToken
      );

      if (result.success) {
        dialogManager.setOpenDialog(DIALOG_NAMES.TEXT, {
          message: t('contentAddedMessage'),
          size: 'tiny',
          title: t('contentAddedTitle')
        },
        () => {
          postDeepLinkResponse(result.deeplinkUrl, result.jwt);
          window.close();
        });
      } else {
        dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
          errorLabel: t('addContentErrorTitle'),
          errorMessage: t('submissionError')
        },
        () => {
          dialogManager.closeDialog(DIALOG_NAMES.ERROR);
          window.close();
        });
        setIsSubmittingLtiContentPickerSelections(false);
      }
    } catch (error) {
      console.error('Error submitting selections:', error);
      dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
        errorLabel: t('addContentErrorTitle'),
        errorMessage: t('submissionError')
      },
      () => {
        dialogManager.closeDialog(DIALOG_NAMES.ERROR);
        window.close();
      });
      setIsSubmittingLtiContentPickerSelections(false);
    }
  };

  const postDeepLinkResponse = (deeplinkUrl, jwt) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = deeplinkUrl;

    const jwtInput = document.createElement('input');
    jwtInput.type = 'hidden';
    jwtInput.name = 'JWT';
    jwtInput.value = jwt;

    form.appendChild(jwtInput);
    document.body.appendChild(form);
    form.submit();
  };

  const downloadResource = async (row) => {
    try {
      const downloadUrl = await contentManager.getDownloadResource(row.contentItemId || row.entityId);

      if (downloadUrl && typeof downloadUrl === 'string') {
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
      dialogManager.setOpenDialog(DIALOG_NAMES.ERROR, {
        errorLabel: t('downloadErrorTitle'),
        errorMessage: t('downloadErrorMessage')
      },
      () => dialogManager.closeDialog(DIALOG_NAMES.ERROR));
    }
  };

  const openPreview = (row) => {
    setPreviewCourseElement(row);
  };

  const handlePageChange = async (_event, pageInfo) => {
    courseManager.activePage = pageInfo.activePage;
    await LtiService.searchLtiContentPickerContent();
  };

  const renderTableHeader = () => {
    return (
      <Table.Header className='lti-content-picker-header'>
        <Table.Row>
          <Table.HeaderCell className='cell-content'>{t('content')}</Table.HeaderCell>
          <Table.HeaderCell className='cell-preview'>{t('preview')}</Table.HeaderCell>
          <Table.HeaderCell className='cell-add-to-course'>{t('addToCourse')}</Table.HeaderCell>
          <Table.HeaderCell className='cell-create-assignment'>{t('createAssignment')}</Table.HeaderCell>
          <Table.HeaderCell className='cell-allow-review'>{t('allowReview')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
    );
  };

  const renderTableRow = (rowId) => {
    const row = ltiManager.ltiTableRowMap.get(rowId);

    if (!row) {
      return null;
    }

    const {
      selectedLtiContentPickerAddToCourseIds,
      selectedLtiContentPickerCreateAssignmentIds,
      selectedLtiContentPickerAllowStudentReviewIds
    } = ltiManager;

    const { description, deliveryMode } = row;
    const contentItemDescription = row.contentItemDescription || '';

    const studentAssignable = isStudentAssignable(deliveryMode);
    const teacherOnly = deliveryMode === 'teacher_only';
    const isDownloadable = LtiService.isLtiDownloadableContent(row);

    const isAddedToCourse = selectedLtiContentPickerAddToCourseIds.has(rowId);
    const hasAssignment = selectedLtiContentPickerCreateAssignmentIds.has(rowId);
    const allowsReview = selectedLtiContentPickerAllowStudentReviewIds.has(rowId);

    const LTI_CHECKBOX_POPUP_OFFSET = [10, 0];
    const LTI_CHECKBOX_POPUP_POSITION = 'top right';

    let addToCourseCheckbox = null;
    let assignmentCheckbox = null;
    let studentReviewCheckbox = null;

    if (studentAssignable || teacherOnly || !deliveryMode) {
      const addTooltip = teacherOnly ? t('addToCourseTooltipTeacherOnly') : t('addToCourseTooltip');

      addToCourseCheckbox = PopupService.renderPopup({
        className: 'lti-popup custom-popup zoom-popup',
        content: addTooltip,
        mouseEnterDelay: 1000,
        offset: LTI_CHECKBOX_POPUP_OFFSET,
        position: LTI_CHECKBOX_POPUP_POSITION,
        trigger: (
          <IndeterminateCheckbox
            checked={isAddedToCourse}
            onChange={() => ltiManager.toggleLtiContentPickerAddToCourse(rowId)}
          />
        ),
        wide: false
      });
    }

    const assignmentDisabled = !isAddedToCourse;
    if (studentAssignable) {
      const assignmentTooltip = assignmentDisabled ? t('createAssignmentDisabled') : t('createAssignmentTooltip');

      assignmentCheckbox = PopupService.renderPopup({
        className: 'lti-popup custom-popup zoom-popup',
        content: assignmentTooltip,
        mouseEnterDelay: 1000,
        offset: LTI_CHECKBOX_POPUP_OFFSET,
        position: LTI_CHECKBOX_POPUP_POSITION,
        trigger: (
          <IndeterminateCheckbox
            checked={hasAssignment}
            disabled={assignmentDisabled}
            onChange={() => ltiManager.toggleLtiContentPickerCreateAssignment(rowId)}
          />
        ),
        wide: false
      });
    }

    const reviewDisabled = !isAddedToCourse;
    if (studentAssignable) {
      const reviewTooltip = reviewDisabled ? t('allowReviewDisabled') : t('allowReviewTooltip');

      studentReviewCheckbox = PopupService.renderPopup({
        className: 'lti-popup custom-popup zoom-popup',
        content: reviewTooltip,
        mouseEnterDelay: 1000,
        offset: LTI_CHECKBOX_POPUP_OFFSET,
        position: LTI_CHECKBOX_POPUP_POSITION,
        trigger: (
          <IndeterminateCheckbox
            checked={allowsReview}
            disabled={reviewDisabled}
            onChange={() => ltiManager.toggleLtiContentPickerAllowStudentReview(rowId)}
          />
        ),
        wide: false
      });
    }

    let previewButton = null;
    if (studentAssignable || teacherOnly) {
      if (isDownloadable) {
        previewButton = (
          <PopupButton
            basic
            buttonLabel={t('download')}
            onClick={() => downloadResource(row)}
            primary />
        );
      } else {
        previewButton = (
          <PopupButton
            basic
            buttonLabel={t('preview')}
            onClick={() => openPreview(row)}
            primary />
        );
      }
    }

    const itemName = UtilityService.reactHtmlParserWrapper(row.name)?.parsed;
    const subtitle = UtilityService.reactHtmlParserWrapper(description || contentItemDescription)?.parsed;

    const popup = (
      <div className='lti-assign-course-label'>
        <div className='lti-assign-course-name'>{itemName}</div>
        {subtitle && <div className='lti-assign-course-description'>{subtitle}</div>}
      </div>
    );

    const label = (
      <div className='lti-assign-course-label'>
        <div className='lti-assign-course-name ellipsis'>{itemName}</div>
        {subtitle && <div className='lti-assign-course-description ellipsis'>{subtitle}</div>}
      </div>
    );

    return (
      <Table.Row key={rowId}>
        <Table.Cell className='cell-content'>
          <Popup
            className='lti-popup custom-popup zoom-popup'
            content={(<div>{popup}</div>)}
            hoverable
            mouseEnterDelay={1000}
            on='hover'
            trigger={(
              <div className='lti-content-cell'>
                <div className='label-div'>{label}</div>
              </div>
            )}
            wide='very' />
        </Table.Cell>
        <Table.Cell className='cell-preview'><span>{previewButton}</span></Table.Cell>
        <Table.Cell className='cell-add-to-course'>{addToCourseCheckbox}</Table.Cell>
        <Table.Cell className='cell-create-assignment'>{assignmentCheckbox}</Table.Cell>
        <Table.Cell className='cell-allow-review'>{studentReviewCheckbox}</Table.Cell>
      </Table.Row>
    );
  };

  const renderTableBody = () => {
    const { ltiContentPickerSearchRowIds } = ltiManager;

    if (courseManager.isLoading) {
      return (
        <Table.Body className='lti-content-picker-body'>
          <Table.Row>
            <Table.Cell colSpan={5}>
              <Loader active inline='centered' />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      );
    }

    if (ltiContentPickerSearchRowIds.length === 0) {
      return (
        <Table.Body className='lti-content-picker-body'>
          <Table.Row>
            <Table.Cell colSpan={5}>
              <div className='lti-search-zero-state'>{t('noResultsZeroStateMsg')}</div>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      );
    }

    return (
      <Table.Body className='lti-content-picker-body'>
        {ltiContentPickerSearchRowIds.map((rowId) => renderTableRow(rowId))}
      </Table.Body>
    );
  };

  const { isLtiUnauthorized, ltiContentPickerSearchRowIds } = ltiManager;
  const { activePage, isLoading, totalItems, totalPages } = courseManager;
  const itemCount = ltiContentPickerSearchRowIds.length;

  if (isLtiUnauthorized) {
    return (
      <div className='LtiContentPickerSearchView'>
        <div className='status-message'>{t('unauthorized')}</div>
      </div>
    );
  }

  const resultsCountText = itemCount > 0 ? t('showingResults', { count: itemCount, total: totalItems }) : '';

  return (
    <>
      <div className='LtiContentPickerSearchView'>
        <div className='lti-content-picker-search-content'>
          <div className='lti-content-picker-search-filter'>
            <Button basic className='back-to-content-button' onClick={handleBackToContent} primary>
              {t('backToContent')}
            </Button>
            
            <AssessmentItemFilter
              handleFetchAssessmentItems={async () => {}}
              ltiContentPickerSearch={true}
              multiSelect={true} />
          </div>

          <div className='lti-content-picker-search-results'>
          <div className='lti-content-picker-controls'>
            <div className='lti-content-picker-search-title'>{t('selectItems')}</div>
            <Button onClick={handleAddContent} primary>
                  {t('addAllSelectedContent')}
                </Button>
                </div>
            <div className='lti-content-picker-controls'>
              <div className='lti-controls-left'>
                <span className='results-count'>{resultsCountText}</span>
              </div>
              <div className='lti-controls-right'>
                {totalPages > 1 && (
                  <Pagination
                    activePage={activePage}
                    onPageChange={handlePageChange}
                    totalPages={totalPages} />
                )}
                
              </div>
            </div>

            <div className={classNames('lti-content-picker-table-wrapper', { loading: isLoading })}>
              <Table className='LtiContentPickerTable lti-search-table' compact>
                {renderTableHeader()}
                {renderTableBody()}
              </Table>
            </div>
          </div>
        </div>
      </div>

      {previewCourseElement && (
        <LTIPreview
          close={() => setPreviewCourseElement(null)}
          courseElement={previewCourseElement}
        />
      )}

      {isSubmittingLtiContentPickerSelections && (
        <Dimmer active className='lti-content-picker-dimmer' page>
          <Loader />
        </Dimmer>
      )}
    </>
  );
});

export default withRouter(LtiContentPickerSearchView);
SatCoreRegister('LtiContentPickerSearchView', LtiContentPickerSearchView);
